import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ListMusic, Plus, Sparkles, Trash2, Music, ArrowRight, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAppContext } from '@/App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function PlaylistsPage() {
  const { refreshCounts } = useAppContext();
  const [playlists, setPlaylists] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [crates, setCrates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [suggestFromTrack, setSuggestFromTrack] = useState('random');
  const [suggestFromCrate, setSuggestFromCrate] = useState('all');
  const [suggestName, setSuggestName] = useState('AI Generated Mix');
  const [suggestBpmMin, setSuggestBpmMin] = useState('');
  const [suggestBpmMax, setSuggestBpmMax] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchData = async () => {
    try {
      const [playlistsRes, tracksRes, cratesRes] = await Promise.all([
        axios.get(`${API}/playlists`),
        axios.get(`${API}/tracks`),
        axios.get(`${API}/crates`)
      ]);
      setPlaylists(playlistsRes.data.playlists || []);
      setTracks(tracksRes.data.tracks || []);
      setCrates(cratesRes.data.crates || []);
    } catch (e) {
      console.error('Failed to fetch playlists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }

    try {
      await axios.post(`${API}/playlists`, {
        name: newPlaylistName,
        track_ids: []
      });
      toast.success('Playlist created!');
      setNewPlaylistName('');
      setShowCreateDialog(false);
      fetchData();
      refreshCounts();
    } catch (e) {
      toast.error('Failed to create playlist');
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Delete this playlist?')) return;
    
    try {
      await axios.delete(`${API}/playlists/${playlistId}`);
      toast.success('Playlist deleted');
      fetchData();
      refreshCounts();
    } catch (e) {
      toast.error('Failed to delete playlist');
    }
  };

  const generateSuggestedPlaylist = async () => {
    // Get track count based on source
    const sourceTrackCount = suggestFromCrate !== 'all' 
      ? (crates.find(c => c.id === suggestFromCrate)?.active_track_count ?? (crates.find(c => c.id === suggestFromCrate)?.track_ids?.length || 0))
      : tracks.length;
    
    if (sourceTrackCount < 2) {
      toast.error(suggestFromCrate !== 'all' 
        ? 'Need at least 2 tracks in this crate' 
        : 'Need at least 2 tracks in library');
      return;
    }

    setGenerating(true);
    try {
      const params = new URLSearchParams({ name: suggestName });
      if (suggestFromTrack && suggestFromTrack !== 'random') {
        params.append('start_track_id', suggestFromTrack);
      }
      if (suggestBpmMin) {
        params.append('bpm_min', suggestBpmMin);
      }
      if (suggestBpmMax) {
        params.append('bpm_max', suggestBpmMax);
      }
      if (suggestFromCrate && suggestFromCrate !== 'all') {
        params.append('crate_id', suggestFromCrate);
      }
      
      const response = await axios.post(`${API}/playlists/suggest?${params}`);
      toast.success('Suggested playlist generated!');
      fetchData();
      refreshCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate playlist');
    } finally {
      setGenerating(false);
    }
  };

  const userPlaylists = playlists.filter(p => !p.is_suggested);
  const suggestedPlaylists = playlists.filter(p => p.is_suggested);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 data-page-header="redundant" className="font-heading text-3xl sm:text-4xl font-bold mb-2 text-[#0F172A] tracking-tight">
              Your <span className="gradient-text">playlists</span>
            </h1>
            <p className="font-body text-[#64748B] text-sm">
              Create and manage your DJ sets
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary text-xs sm:text-sm px-3 py-2 sm:px-4" data-testid="create-playlist-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">New Playlist</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-[#E2E8F0]">
                <DialogHeader>
                  <DialogTitle className="font-heading text-[#0F172A]">Create New Playlist</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <Input
                    placeholder="Playlist name..."
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    className="bg-white border-[#E2E8F0] text-[#0F172A]"
                    data-testid="new-playlist-name"
                  />
                  <Button onClick={createPlaylist} className="w-full btn-primary">
                    Create Playlist
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* AI Suggest Section */}
        <div className="glass-pane p-6 mb-10">
          <h2 className="font-heading text-base sm:text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2563FF]" />
            GENERATE SUGGESTED PLAYLIST
          </h2>
          <p className="font-body text-[#64748B] text-sm mb-6">
            AI will create a playlist based on Camelot wheel compatibility and BPM range for smooth transitions
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="font-mono text-xs text-[#64748B] block mb-2">PLAYLIST NAME</label>
              <Input
                placeholder="AI Generated Mix"
                value={suggestName}
                onChange={(e) => setSuggestName(e.target.value)}
                className="bg-white border-[#E2E8F0] text-[#0F172A]"
                data-testid="suggest-playlist-name"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-[#64748B] block mb-2">SOURCE</label>
              <Select value={suggestFromCrate} onValueChange={setSuggestFromCrate}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-[#0F172A]" data-testid="suggest-source">
                  <FolderOpen className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="All tracks" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0] max-h-60">
                  <SelectItem value="all" className="text-[#0F172A] hover:bg-[#EFF6FF]">All Library Tracks</SelectItem>
                  {crates.map(crate => (
                    <SelectItem key={crate.id} value={crate.id} className="text-[#0F172A] hover:bg-[#EFF6FF]">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: crate.color }} />
                        {crate.name} ({crate.active_track_count ?? (crate.track_ids?.length || 0)})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono text-xs text-[#64748B] block mb-2">BPM RANGE</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={suggestBpmMin}
                  onChange={(e) => setSuggestBpmMin(e.target.value)}
                  className="bg-white border-[#E2E8F0] text-[#0F172A] w-full"
                  data-testid="suggest-bpm-min"
                />
                <span className="text-[#64748B]">-</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={suggestBpmMax}
                  onChange={(e) => setSuggestBpmMax(e.target.value)}
                  className="bg-white border-[#E2E8F0] text-[#0F172A] w-full"
                  data-testid="suggest-bpm-max"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-xs text-[#64748B] block mb-2">START TRACK</label>
              <Select value={suggestFromTrack} onValueChange={setSuggestFromTrack}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-[#0F172A]">
                  <SelectValue placeholder="Random start" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0] max-h-60">
                  <SelectItem value="random" className="text-[#0F172A] hover:bg-[#EFF6FF]">Random start</SelectItem>
                  {tracks.map(track => (
                    <SelectItem key={track.id} value={track.id} className="text-[#0F172A] hover:bg-[#EFF6FF]">
                      {track.title} ({track.analysis?.camelot})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={generateSuggestedPlaylist} 
                disabled={generating || tracks.length < 2}
                className="w-full btn-primary"
                data-testid="generate-playlist-btn"
              >
                {generating ? (
                  <>Generating...</>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* User Playlists */}
        <div className="mb-10">
          <h2 className="font-heading text-xl font-bold mb-6">MY PLAYLISTS</h2>
          
          {userPlaylists.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {userPlaylists.map((playlist, index) => (
                <motion.div
                  key={playlist.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/playlist/${playlist.id}`} data-testid={`playlist-${playlist.id}`}>
                    <div className="glass-pane p-6 hover:border-[#2563FF]/40 hover:shadow-[0_20px_50px_-15px_rgba(37,99,255,0.18)] transition-all duration-200 group">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-[#EFF6FF] flex items-center justify-center">
                          <ListMusic className="w-6 h-6 text-[#2563FF]" />
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); deletePlaylist(playlist.id); }}
                          className="p-2 text-[#94A3B8] hover:text-[#2563FF] opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-playlist-${playlist.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <h3 className="font-heading text-lg text-[#0F172A] group-hover:text-[#2563FF] transition-colors">
                        {playlist.name}
                      </h3>
                      <p className="font-mono text-sm text-[#64748B] mt-1">
                        {playlist.active_track_count ?? playlist.track_ids?.length ?? 0} tracks
                      </p>
                      {playlist.description && (
                        <p className="font-body text-[#94A3B8] text-sm mt-2 line-clamp-2">{playlist.description}</p>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 glass-pane">
              <ListMusic className="w-12 h-12 text-[#1E3A8A] mx-auto mb-3" />
              <p className="font-heading text-[#64748B]">No playlists yet</p>
              <p className="font-body text-[#94A3B8] text-sm mt-1">Create one or generate a suggested mix</p>
            </div>
          )}
        </div>

        {/* Suggested Playlists */}
        {suggestedPlaylists.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#2563FF]" />
              AI SUGGESTED PLAYLISTS
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {suggestedPlaylists.map((playlist, index) => (
                <motion.div
                  key={playlist.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/playlist/${playlist.id}`} data-testid={`suggested-playlist-${playlist.id}`}>
                    <div className="bg-white border border-[#2563FF]/30 p-6 hover:border-[#2563FF]/50 transition-colors group">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-[#2563FF]/20 flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-[#2563FF]" />
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); deletePlaylist(playlist.id); }}
                          className="p-2 text-[#94A3B8] hover:text-[#2563FF] opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <h3 className="font-heading text-lg text-[#0F172A] group-hover:text-[#2563FF] transition-colors">
                        {playlist.name}
                      </h3>
                      <p className="font-mono text-sm text-[#64748B] mt-1">
                        {playlist.active_track_count ?? playlist.track_ids?.length ?? 0} tracks
                      </p>
                      <p className="font-body text-[#2563FF]/70 text-xs mt-2">
                        Camelot wheel optimized
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
